import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, CheckCircle2, Phone, Mail, MapPin, ChevronRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";
import { api, formatApiError } from "../lib/api";

/* ---------- Configurazione contatti ---------- */
export const AGENCY_PHONE       = "+39 02 20 17 77";
export const AGENCY_PHONE_SHORT = "02 20 17 77";
export const AGENCY_PHONE_TEL   = "390220177";

/* ---------- Recensioni reali (Google) ---------- */
export const REAL_REVIEWS = [
  { name: "Lucia Galanti",       role: "Cliente da anni",   stars: 5, text: "Cliente da anni e mi sono sempre trovata benissimo. Professionali, disponibili e molto competenti. Sempre chiari e veloci nel trovare la soluzione migliore. Assolutamente consigliati!" },
  { name: "Francesco Fusi",      role: "Cliente da anni",   stars: 5, text: "Cliente da anni e sempre soddisfatto. La differenza la fanno soprattutto le consulenze: ti spiegano tutto in modo chiaro e ti aiutano davvero a scegliere la soluzione migliore." },
  { name: "Avv. Paola Rossini",  role: "Recensione Google", stars: 5, text: "Grande professionalità, disponibilità e trasparenza fin dal primo contatto." },
  { name: "Antonella Salvadori", role: "Cliente storica",   stars: 5, text: "Sono cliente di questa agenzia da molti anni e ho sempre trovato grande professionalità, disponibilità e cortesia." },
];

/* ---------- Breadcrumb minimal con JSON-LD ---------- */
export function Breadcrumb({ current }) {
  // current: { label: "Assicurazione Casa", href: "/assicurazione-casa" }
  useEffect(() => {
    const ld = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home",      "item": "https://dapraassicurazioni.it/" },
        { "@type": "ListItem", "position": 2, "name": "Soluzioni", "item": "https://dapraassicurazioni.it/#servizi" },
        { "@type": "ListItem", "position": 3, "name": current.label, "item": "https://dapraassicurazioni.it" + current.href },
      ],
    };
    let el = document.getElementById("ld-breadcrumb");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "ld-breadcrumb";
      document.head.appendChild(el);
    }
    el.text = JSON.stringify(ld);
    return () => {
      const old = document.getElementById("ld-breadcrumb");
      if (old) old.remove();
    };
  }, [current.label, current.href]);

  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="breadcrumb"
      className="border-b border-slate-200/60 bg-[#FAF9F6]/40"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-3 sm:py-4">
        <ol className="flex items-center gap-2 sm:gap-2.5 text-[11px] sm:text-xs uppercase tracking-[0.15em] text-slate-500">
          <li>
            <Link to="/" data-testid="bc-home" className="hover:text-slate-900 transition-colors">Home</Link>
          </li>
          <li aria-hidden="true" className="text-slate-300">›</li>
          <li>
            <Link to="/#servizi" data-testid="bc-soluzioni" className="hover:text-slate-900 transition-colors">Soluzioni</Link>
          </li>
          <li aria-hidden="true" className="text-slate-300">›</li>
          <li aria-current="page" className="text-slate-900 font-medium truncate" data-testid="bc-current">
            {current.label}
          </li>
        </ol>
      </div>
    </nav>
  );
}

/* ---------- Hook SEO meta ---------- */
export function useSeoMeta({ title, description, keywords, canonical, ogUrl, jsonLd, jsonLdId }) {
  useEffect(() => {
    if (title) document.title = title;
    const ensureMeta = (name, content, attr = "name") => {
      if (!content) return;
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    ensureMeta("description", description);
    ensureMeta("keywords", keywords);
    ensureMeta("og:title", title, "property");
    ensureMeta("og:description", description, "property");
    ensureMeta("og:type", "website", "property");
    ensureMeta("og:url", ogUrl || canonical, "property");

    if (canonical) {
      let el = document.querySelector('link[rel="canonical"]');
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
      el.setAttribute("href", canonical);
    }

    if (jsonLd && jsonLdId) {
      let ld = document.getElementById(jsonLdId);
      if (!ld) {
        ld = document.createElement("script");
        ld.type = "application/ld+json";
        ld.id = jsonLdId;
        document.head.appendChild(ld);
      }
      ld.text = JSON.stringify(jsonLd);
    }
    return () => {
      if (jsonLdId) {
        const old = document.getElementById(jsonLdId);
        if (old) old.remove();
      }
    };
  }, [title, description, keywords, canonical, ogUrl, jsonLd, jsonLdId]);
}

/* ---------- Building blocks ---------- */
export function Stars({ n = 5, size = 16 }) {
  return (
    <div className="flex gap-0.5 text-[#C5A059]" aria-label={`${n} stelle`}>
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} size={size} fill="#C5A059" strokeWidth={0} />
      ))}
    </div>
  );
}

export function ReviewCard({ r }) {
  return (
    <div className="bg-white border border-slate-200 p-7 sm:p-8 transition-all hover:shadow-xl hover:-translate-y-0.5">
      <Stars n={r.stars} />
      <p className="mt-6 text-[15px] sm:text-base text-slate-700 leading-[1.75]">"{r.text}"</p>
      <div className="mt-7 pt-5 border-t border-slate-100">
        <div className="font-medium text-slate-900">{r.name}</div>
        <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-1">{r.role}</div>
      </div>
    </div>
  );
}

export function TestimonialsSection({ heading = "Cosa dicono di noi." }) {
  return (
    <section className="bg-white border-y border-slate-200 py-14 sm:py-20 md:py-28">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <div className="overline">Recensioni clienti</div>
            <h2 className="mt-3 sm:mt-4 font-serif text-[1.75rem] leading-tight sm:text-4xl md:text-5xl tracking-tight text-slate-900">{heading}</h2>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Stars n={5} size={18} />
            <span className="font-medium text-slate-900">5/5</span>
            <span>· Recensioni Google</span>
          </div>
        </div>
        <div className="mt-10 sm:mt-14 grid sm:grid-cols-2 gap-6 sm:gap-8">
          {REAL_REVIEWS.map((r, i) => <ReviewCard key={i} r={r} />)}
        </div>
        <p className="mt-10 text-center text-xs text-slate-500">
          Recensioni raccolte dai nostri clienti su Google.
        </p>
      </div>
    </section>
  );
}

export function FaqSection({ heading, faqs }) {
  return (
    <section className="py-14 sm:py-20 md:py-28">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="text-center">
          <div className="overline">Domande frequenti</div>
          <h2 className="mt-3 sm:mt-4 font-serif text-[1.75rem] leading-tight sm:text-4xl md:text-5xl tracking-tight text-slate-900">{heading}</h2>
        </div>
        <Accordion type="single" collapsible className="mt-10 sm:mt-14">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="border-b border-slate-200">
              <AccordionTrigger className="text-left font-serif text-base sm:text-lg md:text-xl text-slate-900 hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-[14.5px] sm:text-sm md:text-base leading-[1.75] pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------- Lead form ---------- */
function FormInput({ testid, ...props }) {
  return (
    <input
      data-testid={testid}
      {...props}
      className="w-full h-12 px-3 bg-white border border-slate-300 focus:border-slate-900 focus:outline-none text-base sm:text-sm"
    />
  );
}

export function LeadForm({ quoteType = "altro", leadTag = "" }) {
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "", note: "", privacy_accepted: false });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy_accepted) {
      toast.error("Per favore accetta l'informativa privacy");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        quote_type: quoteType,
        nome: form.nome,
        cognome: form.cognome,
        email: form.email,
        telefono: form.telefono,
        note: leadTag ? `${leadTag} ${form.note}` : form.note,
        privacy_accepted: form.privacy_accepted,
      };
      if (quoteType === "auto") body.auto = { marca: "Da definire", modello: "Da definire", note: form.note };
      if (quoteType === "casa") body.casa = { indirizzo: "Da definire", citta: "Milano", note: form.note };
      if (quoteType === "vita") body.vita = { prodotti: ["Da definire"], note: form.note };
      if (quoteType === "altro") body.altro = { tipologia: "Lead landing", descrizione: form.note };

      await api.post("/quotes", body);

      try {
        if (typeof window !== "undefined") {
          window._iub = window._iub || {};
          window._iub.cons_instructions = window._iub.cons_instructions || [];
          window._iub.cons_instructions.push(["submit", {
            subject: { email: form.email },
            consent: { legal_notices: [{ identifier: "privacy_policy" }] },
          }]);
        }
      } catch (_) { /* */ }

      setDone(true);
      toast.success("Richiesta ricevuta — ti ricontattiamo a breve.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white border border-slate-200 p-8 text-center">
        <div className="w-14 h-14 mx-auto bg-slate-900 text-[#C5A059] flex items-center justify-center">
          <CheckCircle2 size={24} />
        </div>
        <h3 className="mt-6 font-serif text-2xl text-slate-900">Grazie!</h3>
        <p className="mt-3 text-slate-600">
          Abbiamo ricevuto la tua richiesta. Ti ricontatteremo a breve all'indirizzo <strong>{form.email}</strong>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 p-5 sm:p-6 md:p-8 space-y-4" data-testid="lead-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-700 font-medium">Nome *</label>
          <div className="mt-2"><FormInput required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-700 font-medium">Cognome *</label>
          <div className="mt-2"><FormInput required value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} /></div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-700 font-medium">Email *</label>
          <div className="mt-2"><FormInput type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-slate-700 font-medium">Telefono *</label>
          <div className="mt-2"><FormInput type="tel" required value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="+39 ..." /></div>
        </div>
      </div>
      <div>
        <label className="text-xs uppercase tracking-wide text-slate-700 font-medium">Messaggio (opzionale)</label>
        <textarea
          value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })}
          rows={3}
          className="mt-2 w-full px-3 py-2 bg-white border border-slate-300 focus:border-slate-900 focus:outline-none text-base sm:text-sm"
        />
      </div>
      <label className="flex items-start gap-3 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={form.privacy_accepted}
          onChange={(e) => setForm({ ...form, privacy_accepted: e.target.checked })}
          className="mt-1 w-4 h-4 accent-slate-900"
        />
        <span>
          Ho letto l'
          <a href="/privacy.pdf" target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2 text-[#C5A059] hover:text-slate-900">informativa privacy</a>
          {" "}e acconsento al trattamento dei miei dati per essere ricontattato. <span className="text-[#C5A059]">*</span>
        </span>
      </label>
      <button
        type="submit"
        disabled={submitting}
        className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white h-12 text-sm tracking-wide transition-colors"
      >
        {submitting && <Loader2 size={16} className="animate-spin" />}
        Invia richiesta
      </button>
    </form>
  );
}

/* ---------- Final CTA con form + contatti ---------- */
export function FinalCtaSection({ overline = "Contattaci", heading, subtitle, quoteType, leadTag }) {
  return (
    <section className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-14 sm:py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-12">
        <div className="md:col-span-5">
          <div className="overline" style={{ color: "#C5A059" }}>{overline}</div>
          <h2 className="mt-3 sm:mt-4 font-serif text-[1.75rem] leading-tight sm:text-4xl md:text-5xl">{heading}</h2>
          {subtitle && <p className="mt-5 sm:mt-6 text-[15px] sm:text-base text-slate-300 leading-[1.8] max-w-md">{subtitle}</p>}

          <ul className="mt-8 sm:mt-10 space-y-4 text-sm text-slate-300">
            <li className="flex items-start gap-3">
              <Phone size={16} className="mt-0.5 text-[#C5A059] shrink-0" />
              <a href={`tel:+${AGENCY_PHONE_TEL}`} className="hover:text-white">{AGENCY_PHONE}</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={16} className="mt-0.5 text-[#C5A059] shrink-0" />
              <a href="mailto:info@dapraassicurazioni.it" className="hover:text-white break-all">info@dapraassicurazioni.it</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 text-[#C5A059] shrink-0" />
              <span>Via Lambro, 4 — 20129 Milano</span>
            </li>
          </ul>
        </div>

        <div className="md:col-span-7">
          <LeadForm quoteType={quoteType} leadTag={leadTag} />
        </div>
      </div>
    </section>
  );
}

/* ---------- Hero CTA pulsanti ---------- */
export function HeroCtas({ ctaPrimaryLabel, ctaPrimaryHref }) {
  return (
    <div className="mt-7 sm:mt-8">
      <Link
        to={ctaPrimaryHref}
        className="inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white h-12 px-7 text-sm tracking-wide transition-colors"
      >
        {ctaPrimaryLabel} <ChevronRight size={16} />
      </Link>
    </div>
  );
}

/* ---------- Trust strip (stats) ---------- */
export function TrustStrip({ items }) {
  return (
    <div className="mt-10 sm:mt-12 grid grid-cols-3 gap-4 sm:gap-6 max-w-md">
      {items.map((it, i) => (
        <div key={i} className="border-l border-slate-200 pl-3 sm:pl-4">
          <div className="font-serif text-2xl sm:text-3xl text-slate-900 leading-none">{it.value}</div>
          <div className="overline mt-2 text-[10px] leading-tight">{it.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Hero shared ---------- */
export function LandingHero({
  eyebrow,
  title,
  description,
  imageSrc,
  imageAlt,
  ctaPrimaryLabel,
  ctaPrimaryHref,
  trustItems,
}) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-white to-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 pt-8 sm:pt-14 md:pt-24 pb-14 sm:pb-20 md:pb-28 grid md:grid-cols-12 gap-10 md:gap-12 items-center">
        {/* Image mobile: in cima */}
        <div className="md:hidden -mx-4 sm:-mx-6">
          <div className="aspect-[16/10] overflow-hidden">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </div>

        <div className="md:col-span-7">
          {eyebrow && (
            <div className="overline mb-4 flex items-center gap-2">
              <ShieldCheck size={14} className="text-[#C5A059]" /> {eyebrow}
            </div>
          )}
          <h1 className="font-serif text-[2.25rem] leading-[1.1] sm:text-5xl md:text-6xl md:leading-[1.05] tracking-tight text-slate-900">
            {title}
          </h1>
          <p className="mt-5 md:mt-7 text-[15px] sm:text-base md:text-lg text-slate-600 leading-[1.75] max-w-xl">
            {description}
          </p>
          <HeroCtas
            ctaPrimaryLabel={ctaPrimaryLabel}
            ctaPrimaryHref={ctaPrimaryHref}
          />
          {trustItems && <TrustStrip items={trustItems} />}
        </div>

        {/* Image desktop */}
        <div className="md:col-span-5 hidden md:block">
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Coverage grid ---------- */
export function CoverageGrid({ eyebrow, heading, intro, items, cols = 3 }) {
  const colsClass = cols === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3";
  return (
    <section className="bg-white border-y border-slate-200 py-14 sm:py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          {eyebrow && <div className="overline">{eyebrow}</div>}
          <h2 className="mt-3 sm:mt-4 font-serif text-[1.75rem] leading-tight sm:text-4xl md:text-5xl tracking-tight text-slate-900">
            {heading}
          </h2>
          {intro && (
            <p className="mt-4 sm:mt-5 text-[15px] sm:text-base text-slate-600 leading-[1.75]">{intro}</p>
          )}
        </div>
        <div className={`mt-10 sm:mt-14 grid ${colsClass} gap-5 sm:gap-6`}>
          {items.map((b) => {
            const Icon = b.icon;
            return (
              <div
                key={b.title}
                className="group p-6 sm:p-7 bg-[#FAF9F6] border border-slate-200 hover:border-slate-900 transition-all hover:shadow-lg"
              >
                <span className="w-11 h-11 sm:w-12 sm:h-12 flex items-center justify-center bg-slate-900 text-[#C5A059] group-hover:bg-[#C5A059] group-hover:text-slate-900 transition-colors">
                  <Icon size={20} />
                </span>
                <h3 className="mt-5 font-serif text-xl sm:text-2xl text-slate-900">{b.title}</h3>
                <p className="mt-3 text-[14.5px] sm:text-sm text-slate-600 leading-[1.75]">{b.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------- Why-us section ---------- */
export function WhyUsSection({ eyebrow, heading, body, items }) {
  return (
    <section className="py-14 sm:py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 grid md:grid-cols-12 gap-10 md:gap-14 items-start">
        <div className="md:col-span-5">
          {eyebrow && <div className="overline">{eyebrow}</div>}
          <h2 className="mt-3 sm:mt-4 font-serif text-[1.75rem] leading-tight sm:text-4xl md:text-5xl tracking-tight text-slate-900">
            {heading}
          </h2>
          {Array.isArray(body)
            ? body.map((p, i) => (
                <p key={i} className="mt-5 sm:mt-6 text-[15px] sm:text-base text-slate-600 leading-[1.8]">
                  {p}
                </p>
              ))
            : <p className="mt-5 sm:mt-6 text-[15px] sm:text-base text-slate-600 leading-[1.8]">{body}</p>}
        </div>
        <div className="md:col-span-7 grid sm:grid-cols-2 gap-5 sm:gap-6">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="border-l-2 border-[#C5A059] pl-5 py-2">
                <Icon size={18} className="text-[#C5A059]" />
                <div className="mt-3 font-serif text-lg text-slate-900">{it.title}</div>
                <p className="text-[14.5px] sm:text-sm text-slate-500 mt-2 leading-[1.75]">{it.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
